import Game from '../../Game';
import ResourceDriverManager, { ResourceEvent, IResourceDriverManagerOptions } from './ResourceDriverManager';
import { ResourceID, AtlasKey, ResourceURL, FramesHash } from './IResourceDriver';
import { GameJSON, JSONFilePathToKey } from '../../GameJSON';
import { isWebPSupported, shouldLoadAsWebP } from '../WebPFunctions';
import { INTERMEDIATE_ASSETS_SCALE } from './intermediateAssets';
import { getAtlasByUrl } from './contexts';
import { isNative } from 'Utility/Platform';
import { report } from 'report';

export enum ResourceDriver {
    PRIMARY = 0, // atlas, levels, worlds...
    SECONDARY = 1, // sounds...
    TERTIARY = 2, // avatars...
}
const driverLevels = [ResourceDriver.PRIMARY, ResourceDriver.SECONDARY, ResourceDriver.TERTIARY];
export const isLocalHost: boolean = location.hostname === 'localhost' || location.hostname === '127.0.0.1';

export type Atlas = {
    frames: Record<string, {}>;
    meta: {
        image: string;
    };
};

export default class ResourceManager {
    private readonly cache = new Map<ResourceID | AtlasKey, unknown>();
    private readonly drivers: ResourceDriverManager[];

    constructor() {
        this.drivers = driverLevels.map(driverLevel => {
            let driverSettings: IResourceDriverManagerOptions = { maxAttempts: 5 };
            if (driverLevel === ResourceDriver.TERTIARY) {
                driverSettings = {
                    concurrency: 5,
                    autoStart: true,
                    maxAttempts: 3,
                };
            }
            const driver = new ResourceDriverManager((resourceIDOrAtlasKey, loadedData) => {
                this.cacheResource(resourceIDOrAtlasKey, loadedData, driver);
            }, driverSettings);
            return driver;
        });
    }

    public add(
        resourceID: ResourceID,
        optPath?: ResourceURL,
        driver?: ResourceDriver,
        forceDefaultExt?: boolean // if true, do not transform to `webp`
    ): void {
        let name: string = resourceID;
        let url: ResourceURL | undefined = optPath;
        const atlas = getAtlasByUrl(url);

        if (atlas) {
            const intermediateUrl = `${url.slice(0, -'.json'.length)}@${INTERMEDIATE_ASSETS_SCALE}x.json`;
            const intermediateAtlas = getAtlasByUrl(intermediateUrl);
            const baseUrl = url
                .split('/')
                .slice(0, -1)
                .join('/');

            if (intermediateAtlas && !isNative()) {
                this.cacheResource(name, intermediateAtlas);
                url = `${baseUrl}/${intermediateAtlas.meta.image}`;
            } else {
                this.cacheResource(name, atlas);
                url = `${baseUrl}/${atlas.meta.image}`;
            }

            name = `${name}_image`;
        }

        // TODO: check if url/path is the same
        if (this.existsOrQueued(name)) return;

        if (
            !forceDefaultExt &&
            isWebPSupported() &&
            url &&
            url.lastIndexOf('.png') === url.length - '.png'.length &&
            shouldLoadAsWebP(null, url.substr(url.lastIndexOf('.') + 1), name, url)
        ) {
            url += '.webp';
        }
        const path = url || name;
        if (!isLocalHost) {
            if (JSONFilePathToKey[path]) {
                this.cacheResource(name, JSON.parse(GameJSON[JSONFilePathToKey[path]]));
                return;
            } else if (GameJSON[name]) {
                this.cacheResource(name, JSON.parse(GameJSON[name]));
                return;
            }
        }
        this.drivers[driver || ResourceDriver.PRIMARY].add(name, path);
    }

    // TODO: clean up
    /** @deprecated */
    public readonly addIfNeeded = this.add;

    public stop(driver?: ResourceDriver) {
        this.drivers[driver || ResourceDriver.PRIMARY].stop();
    }

    public async load(driver?: ResourceDriver) {
        try {
            await this.drivers[driver || ResourceDriver.PRIMARY].load();
        } catch (e) {
            console.warn(e);
        }
    }

    public loadFullQualityAtlases(): Promise<FramesHash> {
        return this.drivers[ResourceDriver.PRIMARY].loadFullQualityAtlases();
    }

    public async startOtherDriversLoad() {
        this.load(ResourceDriver.SECONDARY);
        this.load(ResourceDriver.TERTIARY);
    }

    public async wait(resourceID: ResourceID): Promise<void> {
        // If already exists and it's loaded resolve the promise.
        if (this.existsAndLoaded(resourceID)) {
            return Promise.resolve();
        }

        // Search in which driver the resource is it, if it's not in any of them reject.
        const driver = this.drivers.find(d => d.isLoading(resourceID));
        if (!driver) {
            return Promise.reject(new Error(`Resource driver for resource '${resourceID}' not found`));
        }

        // Make sure that the driver is loading the resources.
        driver.load();

        // Wait for...
        while (!this.existsAndLoaded(resourceID) && driver.isLoading(resourceID)) {
            await Game.ticker.setTimeoutPromise(100);
        }

        // Resolve or reject the promise.
        if (this.existsAndLoaded(resourceID)) {
            return Promise.resolve();
        } else {
            return Promise.reject(new Error(`Failed to wait for resource '${resourceID}'`));
        }
    }

    public existsOrQueued(resourceID: ResourceID | AtlasKey): boolean {
        return this.existsAndLoaded(resourceID) || this.drivers.some(driver => driver.isLoading(resourceID));
    }

    public existsAndLoaded(resourceID: ResourceID | AtlasKey): boolean {
        return this.cache.has(resourceID);
    }

    public get<T>(resourceID: ResourceID | AtlasKey): T {
        const resource: any = this.cache.get(resourceID);
        if (resource === undefined) {
            report('Resource not found', {
                once: false,
                sampleRate: 0.1,
                tags: {
                    resourceID,
                },
                fingerprint: 'NoResourceFound:ResourceManager.get',
            });
        }
        return resource;
    }

    public set(resourceIDOrAtlasKey: ResourceID | AtlasKey, resource: unknown): void {
        this.cacheResource(resourceIDOrAtlasKey, resource);
    }

    public cacheResource(
        resourceIDOrAtlasKey: ResourceID | AtlasKey,
        resource: unknown,
        sourceDriver?: ResourceDriverManager
    ) {
        this.cache.set(resourceIDOrAtlasKey, resource);

        // Inform drivers that resource has been cached
        for (const driver of this.drivers) {
            if (driver === sourceDriver) continue;
            driver.informResourceCached(resourceIDOrAtlasKey);
        }
    }

    public addProgressListener(
        listener: (progress: number, resource?: PIXI.loaders.Resource) => void,
        _driver?: ResourceDriver
    ) {
        const driver = this.drivers[_driver ?? ResourceDriver.PRIMARY];
        driver.on(ResourceEvent.PROGRESS, listener);
        return revokeListener;
        function revokeListener() {
            driver.off(ResourceEvent.PROGRESS, listener);
        }
    }
}
